/**
 * Document-side helpers of the curve editor (W2-04b curve-editor; DESIGN.md §4.7, §5.6.2-5.6.3, §6.9, §6.11).
 *
 * Pure functions over the document, the easing catalogue and the unit tables, shared by the column view, the
 * gestures, the commands and the header:
 *  - column ids: W2-04a's motion column instance ids (`motion:<line>:<prop>:<layer>`, contracted) and this
 *    package's own instance ids (`curve:<line>:<prop>:<layer>`, `curve:<line>:<prop>:channel`,
 *    `curve:<line>:<prop>:pair`, with `:wide` in the Animate layout);
 *  - W2-04a's Guided pair columns (Position X | Y, Size W | H on image-like lines), which the editor shows whole;
 *  - which properties have a curve (numeric tracks), which may have layers and which may use a Bezier;
 *  - the layer tracks of a property, the keys of a track, the easing use of a track (speed modes, Guided);
 *  - friendly value formatting (units of DESIGN.md §6.9) and easing labels (plain words in Guided, ids in Full);
 *  - a least-squares cubic Bezier fit for "Turn into a custom curve".
 */

import { Ease } from '../../core/ease/index';
import { catalogEntry } from '../../core/ease/catalog';
import type { BezierPoints, Ease as EaseValue, EaseUse } from '../../core/ease/types';
import { isUiMover, kindOfLine } from '../../core/model/lineKind';
import { PROP_SPECS, isLineProp } from '../../core/model/props';
import { toFriendly, Units } from '../../core/model/units';
import type {
  ChartDoc,
  DocIndexes,
  FriendlyUnit,
  Key,
  KeyId,
  LineId,
  LineProp,
  TrackId,
  UnitContext,
} from '../../core/model/types';
import type { I18n } from '../../i18n/types';
import type { ColumnRef, ExperienceLevel } from '../../state/types';
import type { ValueRange } from '../../render/timeline/motion/curveEditor';
import { guidedPairOf as motionPairOf } from '../../render/timeline/motion/pairs';
import { m } from './curveEditor.messages';

// ---------------------------------------------------------------------------------------------------------------
// Ids
// ---------------------------------------------------------------------------------------------------------------

/** Column def id of the expanded curve editor (ARCHITECTURE.md §7.7). */
export const CURVE_DEF_ID = 'motion-expanded';
/** Placement: right after the motion columns (order 30). */
export const CURVE_ORDER = 31;
/** The UI creates at most this many layers per property (DESIGN.md §5.6.3; imported charts may have more). */
export const MAX_UI_LAYERS = 4;
/** Feature id (settings `ext.curve-editor.*`). */
export const FEATURE_ID = 'curve-editor';

/** W2-04a's motion column instance id (contracted format). */
export function motionColumnId(ref: ColumnRef): string {
  return `motion:${String(ref.line)}:${ref.prop}:${String(ref.layer)}`;
}

function parseIntStrict(s: string | undefined): number | null {
  if (s === undefined || !/^-?\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) ? n : null;
}

/** Parses `motion:<line>:<prop>:<layer>` (null for any other id). */
export function parseMotionColumnId(id: string | null | undefined): ColumnRef | null {
  if (typeof id !== 'string') return null;
  const parts = id.split(':');
  if (parts.length !== 4 || parts[0] !== 'motion') return null;
  const line = parseIntStrict(parts[1]);
  const prop = parts[2];
  const layer = parseIntStrict(parts[3]);
  if (line === null || line < 0 || layer === null || layer < 0 || !isLineProp(prop)) return null;
  return { line: line as LineId, prop, layer: PROP_SPECS[prop].layered ? layer : 0 };
}

export interface CurveIdOptions {
  /** Pair view (`ref` is the pair's first property). */
  readonly pair?: boolean;
  /** Animate layout: the wider stand-in for the floating editor gets its own instance (and so its own width). */
  readonly wide?: boolean;
}

/**
 * This package's instance id: one per expanded column (single layer), per property (channel view) or per pair (pair
 * view), with a `:wide` suffix in the Animate layout.
 */
export function curveInstanceId(ref: ColumnRef, channel: boolean, opts: CurveIdOptions = {}): string {
  const mode = channel ? 'channel' : opts.pair === true ? 'pair' : String(ref.layer);
  return `curve:${String(ref.line)}:${ref.prop}:${mode}${opts.wide === true ? ':wide' : ''}`;
}

/**
 * W2-04a's Guided pair column containing a column, or null: on the active line, Guided shows Position X | Y as one
 * column (always) and Size W | H as one column on image-like lines (texture, GIF, text, UI movers), both on layer 0.
 * W2-04a hides that whole column while either lane is expanded, so the editor shows both lanes.
 */
export function guidedPairOf(
  doc: ChartDoc,
  idx: DocIndexes,
  ref: ColumnRef,
  level: ExperienceLevel,
  activeLine: LineId | null,
): readonly [LineProp, LineProp] | null {
  if (level !== 'guided' || ref.layer !== 0 || ref.line !== activeLine || !doc.lines.has(ref.line)) return null;
  const ctx = unitContextOf(doc, idx, ref.line);
  // The motion columns' own rule (src/render/timeline/motion/pairs.ts, shared with W2-04a).
  return motionPairOf(ref.prop, ctx.kind, ctx.uiMover)?.props ?? null;
}

export function sameColumn(a: ColumnRef | null, b: ColumnRef | null): boolean {
  return a !== null && b !== null && a.line === b.line && a.prop === b.prop && a.layer === b.layer;
}

// ---------------------------------------------------------------------------------------------------------------
// Property capabilities
// ---------------------------------------------------------------------------------------------------------------

/** Only numeric tracks have a curve (colour and text are edited in the columns and the inspector). */
export function isNumericProp(prop: LineProp): boolean {
  return PROP_SPECS[prop].valueType === 'number';
}

/** Properties summed over additive layers (Position X/Y, Rotation, Opacity, Scroll speed). */
export function isLayered(prop: LineProp): boolean {
  return PROP_SPECS[prop].layered;
}

/** Bezier handles are never offered on Scroll speed (Phira's 1.7 integrator panics; DESIGN.md §5.6.7). */
export function bezierAllowed(prop: LineProp): boolean {
  return prop !== 'speed';
}

/** Value range a fitted axis always includes (opacity 0..255; speed from 0). */
export function naturalRange(prop: LineProp): ValueRange | null {
  if (prop === 'alpha') return { lo: 0, hi: 255 };
  if (prop === 'speed') return { lo: 0, hi: 0 };
  return null;
}

/** Ease guard of a track (DESIGN.md §6.11): speed mode and level decide what is offered. */
export function easeUseFor(doc: ChartDoc, prop: LineProp, level: ExperienceLevel): EaseUse {
  if (prop !== 'speed') return 'general';
  if (doc.pkg.speedMode === 'rpe170') return 'speed170';
  return level === 'guided' ? 'speedLegacyGuided' : 'speedLegacy';
}

// ---------------------------------------------------------------------------------------------------------------
// Layers, tracks and keys
// ---------------------------------------------------------------------------------------------------------------

export interface LayerTrack {
  readonly layer: number;
  readonly track: TrackId;
}

/** Existing tracks of a property on a line, by layer index. */
export function layerTracks(doc: ChartDoc, idx: DocIndexes, line: LineId, prop: LineProp): LayerTrack[] {
  const ids = idx.tracksOfLine(line).get(prop) ?? [];
  const out: LayerTrack[] = [];
  for (const id of ids) {
    const t = doc.tracks.get(id);
    if (t?.owner.kind === 'line') out.push({ layer: t.owner.layer, track: id });
  }
  return out.sort((a, b) => a.layer - b.layer);
}

/** Number of layer slots shown for a property: the highest existing layer + 1 (at least 1). */
export function layerCount(doc: ChartDoc, idx: DocIndexes, line: LineId, prop: LineProp): number {
  if (!isLayered(prop)) return 1;
  let n = 1;
  for (const t of layerTracks(doc, idx, line, prop)) n = Math.max(n, t.layer + 1);
  return n;
}

/** Keys of a track in time order (numeric values only are used by the curve editor). */
export function trackKeys(doc: ChartDoc, idx: DocIndexes, track: TrackId | undefined): Key[] {
  if (track === undefined) return [];
  const out: Key[] = [];
  for (const id of idx.keysByTrack(track)) {
    const k = doc.keys.get(id);
    if (k !== undefined) out.push(k);
  }
  return out;
}

/** The (line, prop, layer) a key belongs to, or null (effect / video variables, missing keys). */
export function columnOfKey(doc: ChartDoc, key: KeyId): ColumnRef | null {
  const k = doc.keys.get(key);
  const t = k === undefined ? undefined : doc.tracks.get(k.track);
  if (t?.owner.kind !== 'line') return null;
  return { line: t.owner.line, prop: t.owner.prop, layer: t.owner.layer };
}

/** Selected keys (or segment start keys) that belong to one track, in time order. */
export function idsOnTrack(doc: ChartDoc, ids: Iterable<KeyId>, track: TrackId): KeyId[] {
  const out: Key[] = [];
  for (const id of ids) {
    const k = doc.keys.get(id);
    if (k?.track === track) out.push(k);
  }
  return out.sort((a, b) => a.t.w - b.t.w || a.t.n / a.t.d - b.t.n / b.t.d || a.id - b.id).map((k) => k.id);
}

/** Display name of a line: its name, else "Line <export index>". */
export function lineLabel(doc: ChartDoc, idx: DocIndexes, line: LineId, i18n: I18n): string {
  const rec = doc.lines.get(line);
  const name = rec?.name.trim() ?? '';
  return name !== '' ? name : i18n.t(m.lineFallback, { index: idx.lineIndex(line) });
}

/** Unit context of a line (kind and UI mover decide Length vs Size, etc.). */
export function unitContextOf(doc: ChartDoc, idx: DocIndexes, line: LineId): UnitContext {
  const rec = doc.lines.get(line);
  if (rec === undefined) return { kind: 'normal', uiMover: false };
  return { kind: kindOfLine(doc, idx, rec), uiMover: isUiMover(rec) };
}

/** Name of a Guided pair ("Position X and Y", "Size W and H"). */
export function pairLabel(pair: readonly LineProp[], i18n: I18n): string {
  return i18n.t(m.pairName, { pair: pair[0] === 'moveX' ? 'position' : 'size' });
}

/** User-facing property name ("Opacity", "Length" on normal lines). */
export function propLabel(prop: LineProp, ctx: UnitContext, i18n: I18n): string {
  if ((prop === 'scaleX' || prop === 'scaleY') && ctx.kind === 'normal' && !ctx.uiMover) {
    return i18n.t(m.propNameNormal, { prop });
  }
  return i18n.t(m.propName, { prop });
}

/** Optional editor name of a layer, else "L<n>" (layer 0 is "L0" like the motion column chips, UX-039). */
export function layerLabel(doc: ChartDoc, line: LineId, layer: number, i18n: I18n): string {
  const named = doc.lines.get(line)?.editor.layerNames[layer] ?? null;
  if (named !== null && named.trim() !== '') return named;
  // Layer 0 is "L0" as in the motion column chips (UX-039: one name for the base layer).
  return i18n.t(m.layerChipShort, { n: layer });
}

// ---------------------------------------------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------------------------------------------

/** Linear display scale of a property (friendly = raw · scale), and its unit. */
export interface DisplayUnit {
  readonly unit: FriendlyUnit;
  readonly scale: number;
}

export function displayUnitOf(prop: LineProp, ctx: UnitContext, mode: 'friendly' | 'rpe'): DisplayUnit {
  if (mode === 'rpe') return { unit: 'raw', scale: 1 };
  const unit = Units.unitFor(prop, ctx);
  const scale = toFriendly(unit, 1) - toFriendly(unit, 0);
  return { unit, scale: Number.isFinite(scale) && scale !== 0 ? scale : 1 };
}

/** Decimals that show a value at a given resolution (step in display units), 0..6. */
export function decimalsFor(step: number): number {
  if (!(step > 0) || !Number.isFinite(step)) return 2;
  return Math.max(0, Math.min(6, Math.ceil(-Math.log10(step) - 1e-9)));
}

/** A number with at most `decimals` decimals, trailing zeros removed, no "-0". */
export function formatNumber(v: number, decimals: number): string {
  if (!Number.isFinite(v)) return String(v);
  const s = v.toFixed(Math.max(0, Math.min(10, decimals)));
  const trimmed = s.includes('.') ? s.replace(/\.?0+$/, '') : s;
  return trimmed === '-0' ? '0' : trimmed;
}

/** Formats a RAW value in the display unit. `step` is the resolution in raw units (default: 3 decimals). */
export function formatValue(raw: number, unit: DisplayUnit, i18n: I18n, step?: number): string {
  const v = raw * unit.scale;
  const decimals = step === undefined ? 3 : decimalsFor(Math.abs(step * unit.scale));
  const text = formatNumber(v, decimals);
  switch (unit.unit) {
    case 'px':
    case 'brushPx':
      return i18n.t(m.unitPx, { v: text });
    case 'deg':
      return i18n.t(m.unitDeg, { v: text });
    case 'opacityPercent':
    case 'lengthPercent':
    case 'sizePercent':
    case 'progressPercent':
      return i18n.t(m.unitPercent, { v: text });
    case 'thickness':
      return i18n.t(m.unitTimes, { v: text });
    case 'speed':
    case 'raw':
      return i18n.t(m.unitPlain, { v: text });
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Easing labels
// ---------------------------------------------------------------------------------------------------------------

export interface EaseLabelContext {
  readonly level: ExperienceLevel;
  /** Speed track in legacy mode: eased segments are baked on export (DESIGN.md §6.11). */
  readonly speedLegacy: boolean;
  /** RPE easing the model keeps but Phira ignores (`src.ignoredEase.easingType`), or null. */
  readonly ignoredEasingType: number | null;
}

function tweenName(id: number, level: ExperienceLevel, i18n: I18n): string {
  const entry = catalogEntry(id);
  if (entry === null || entry.family === 'linear') return i18n.t(m.curveLinear);
  if (level === 'guided' && entry.strength !== null && entry.shape !== null) {
    return i18n.t(m.curveGuided, {
      strength: i18n.t(m.easeStrength, { s: entry.strength }),
      shape: i18n.t(m.easeShape, { s: entry.shape }),
    });
  }
  return i18n.t(m.curveFull, { name: entry.rpeName, id });
}

/** Badge text of a segment: "Linear", "Stay", "Custom", "Soft, slow end" (Guided) or "OutQuad (4)" (Full). */
export function easeLabel(e: EaseValue, ctx: EaseLabelContext, i18n: I18n): string {
  if (e.kind === 'hold') return i18n.t(m.curveHold);
  if (e.kind === 'bezier') return i18n.t(m.curveBezier);
  let name = tweenName(e.id, ctx.level, i18n);
  if (e.id !== 1 && (e.left !== 0 || e.right !== 1)) name = i18n.t(m.curvePart, { name });
  if (ctx.speedLegacy && e.id !== 1) return i18n.t(m.curveBaked, { name });
  if (ctx.ignoredEasingType !== null && ctx.ignoredEasingType > 1) {
    return i18n.t(m.curveIgnored, { name: tweenName(ctx.ignoredEasingType, ctx.level, i18n) });
  }
  return name;
}

/** Normalised whole-curve value of RPE easing `easingType` at u (slice rails): Phira's static tween. */
export function wholeCurveY(easingType: number, u: number): number {
  return Ease.staticY(Ease.rpeToTweenId(easingType), u);
}

// ---------------------------------------------------------------------------------------------------------------
// Bezier fit ("Turn into a custom curve")
// ---------------------------------------------------------------------------------------------------------------

const FIT_SAMPLES = 33;

function fitError(p: BezierPoints, xs: readonly number[], ys: readonly number[]): number {
  let sum = 0;
  for (let i = 0; i < xs.length; i++) {
    const d = Ease.bezierY(p, xs[i] as number) - (ys[i] as number);
    sum += Number.isFinite(d) ? d * d : 1e6;
  }
  return sum;
}

function clampPoints(v: readonly number[]): BezierPoints {
  const c = (x: number): number => Math.min(1, Math.max(0, x));
  const r = (x: number): number => Math.round(x * 1000) / 1000;
  return [r(c(v[0] ?? 0)), r(v[1] ?? 0), r(c(v[2] ?? 1)), r(v[3] ?? 1)];
}

/**
 * Cubic Bezier (x1, x2 in [0, 1], y free) closest to y = f(x) on [0, 1] in the least-squares sense (Nelder-Mead
 * from a few classic starting shapes, evaluated with Phira's own Bezier solver). Returns the points and the largest
 * difference over the samples (in units of the curve's 0..1 range).
 */
export function fitBezier(f: (x: number) => number): { readonly p: BezierPoints; readonly maxError: number } {
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < FIT_SAMPLES; i++) {
    const x = i / (FIT_SAMPLES - 1);
    xs.push(x);
    const y = f(x);
    ys.push(Number.isFinite(y) ? y : 0);
  }
  const cost = (v: readonly number[]): number => fitError(clampPoints(v), xs, ys);
  const starts: number[][] = [
    [0.33, ys[11] ?? 0.33, 0.67, ys[22] ?? 0.67],
    [0.42, 0, 0.58, 1],
    [0.42, 0, 1, 1],
    [0, 0, 0.58, 1],
    [0.25, 0.1, 0.25, 1],
  ];
  let best: number[] = starts[0] as number[];
  let bestCost = cost(best);
  for (const s of starts) {
    const r = nelderMead(cost, s, 160);
    const c = cost(r);
    if (c < bestCost) {
      best = r;
      bestCost = c;
    }
  }
  const p = clampPoints(best);
  let maxError = 0;
  for (let i = 0; i < xs.length; i++) {
    const d = Math.abs(Ease.bezierY(p, xs[i] as number) - (ys[i] as number));
    if (Number.isFinite(d)) maxError = Math.max(maxError, d);
  }
  return { p, maxError };
}

/** Plain Nelder-Mead minimiser (fixed iteration budget). */
function nelderMead(f: (v: readonly number[]) => number, start: readonly number[], iterations: number): number[] {
  const n = start.length;
  let simplex: number[][] = [start.slice()];
  for (let i = 0; i < n; i++) {
    const v = start.slice();
    v[i] = (v[i] ?? 0) + 0.1;
    simplex.push(v);
  }
  let values = simplex.map(f);
  for (let it = 0; it < iterations; it++) {
    const order = values.map((v, i) => [v, i] as const).sort((a, b) => a[0] - b[0]);
    simplex = order.map(([, i]) => simplex[i] as number[]);
    values = order.map(([v]) => v);
    const centroid = new Array<number>(n).fill(0);
    for (let i = 0; i < n; i++)
      for (let j = 0; j < n; j++) centroid[j] = (centroid[j] ?? 0) + (simplex[i]?.[j] ?? 0) / n;
    const worst = simplex[n] as number[];
    const at = (t: number): number[] => centroid.map((c, j) => c + t * (c - (worst[j] ?? 0)));
    const reflected = at(1);
    const fr = f(reflected);
    const fBest = values[0] as number;
    const fSecond = values[n - 1] as number;
    const fWorst = values[n] as number;
    if (fr < fBest) {
      const expanded = at(2);
      const fe = f(expanded);
      simplex[n] = fe < fr ? expanded : reflected;
      values[n] = Math.min(fe, fr);
    } else if (fr < fSecond) {
      simplex[n] = reflected;
      values[n] = fr;
    } else {
      const contracted = at(fr < fWorst ? 0.5 : -0.5);
      const fc = f(contracted);
      if (fc < Math.min(fr, fWorst)) {
        simplex[n] = contracted;
        values[n] = fc;
      } else {
        const best = simplex[0] as number[];
        for (let i = 1; i <= n; i++) {
          simplex[i] = (simplex[i] as number[]).map((x, j) => (best[j] ?? 0) + 0.5 * (x - (best[j] ?? 0)));
          values[i] = f(simplex[i] as number[]);
        }
      }
    }
  }
  let bi = 0;
  for (let i = 1; i < values.length; i++) if ((values[i] as number) < (values[bi] as number)) bi = i;
  return simplex[bi] as number[];
}
